import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Raw, window-level orientation snapshot for the rotation test harness.
 *
 * Everything here is read straight from the platform (screen.orientation,
 * innerWidth/Height, matchMedia, CSS var) rather than through React hooks, so
 * it reflects exactly what the browser reports at the moment an event fires —
 * which is the ground truth needed to diagnose the Android 17 wrong-direction
 * rotation bug.
 */
export interface RotationSnapshot {
  /** screen.orientation.angle (0/90/180/270), 0 when unsupported. */
  angle: number;
  /** screen.orientation.type, e.g. portrait-primary / landscape-secondary. */
  orientationType: string;
  /** Current value of the --force-rotate-angle CSS var on <html>. */
  forceRotateAngle: string;
  /**
   * Legacy pre-fix direction from the screen angle alone (`angle === 180 ? -90 : 90`).
   * This is NOT what production now uses — the shipped resolver is gamma-aware
   * (see forceRotate.ts). Surfaced only to contrast against the live
   * `forceRotateAngle` CSS var, which reflects the real production value.
   */
  legacyAngleDirection: number;
  innerWidth: number;
  innerHeight: number;
  visualWidth: number | undefined;
  visualHeight: number | undefined;
  matchMediaPortrait: boolean;
  isFullscreen: boolean;
  lockSupported: boolean;
}

export interface RotationLogEntry {
  id: number;
  time: string;
  event: string;
  snapshot: RotationSnapshot;
}

const MAX_LOG_ENTRIES = 40;

const readSnapshot = (): RotationSnapshot => {
  const screenOrientation = window.screen?.orientation as
    | (ScreenOrientation & { lock?: (orientation: string) => Promise<void> })
    | undefined;
  const angle = screenOrientation?.angle ?? 0;
  const orientationType = screenOrientation?.type ?? 'unknown';
  const forceRotateAngle =
    getComputedStyle(document.documentElement)
      .getPropertyValue('--force-rotate-angle')
      .trim() || '(unset)';

  return {
    angle,
    orientationType,
    forceRotateAngle,
    legacyAngleDirection: angle === 180 ? -90 : 90,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    visualWidth: window.visualViewport?.width,
    visualHeight: window.visualViewport?.height,
    matchMediaPortrait: window.matchMedia('(orientation: portrait)').matches,
    isFullscreen: !!document.fullscreenElement,
    lockSupported: typeof screenOrientation?.lock === 'function',
  };
};

const formatTime = (): string =>
  new Date().toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });

const snapshotToText = (entry: RotationLogEntry): string => {
  const s = entry.snapshot;
  return (
    `[${entry.time}] ${entry.event} | ` +
    `angle=${s.angle} type=${s.orientationType} ` +
    `forceRotate=${s.forceRotateAngle} legacyDir=${s.legacyAngleDirection} ` +
    `inner=${s.innerWidth}x${s.innerHeight} ` +
    `visual=${s.visualWidth ?? '-'}x${s.visualHeight ?? '-'} ` +
    `mmPortrait=${s.matchMediaPortrait} fullscreen=${s.isFullscreen}`
  );
};

/**
 * Subscribe to every orientation/viewport/fullscreen signal and keep both a
 * live snapshot and a timestamped event log. Used only by the /rotation-test
 * diagnostic page.
 */
export function useRotationDiagnostics(): {
  snapshot: RotationSnapshot;
  log: RotationLogEntry[];
  pushEvent: (event: string) => void;
  clearLog: () => void;
  copyLog: () => Promise<void>;
} {
  const [snapshot, setSnapshot] = useState<RotationSnapshot>(() => readSnapshot());
  const [log, setLog] = useState<RotationLogEntry[]>([]);
  const nextIdRef = useRef(0);

  const pushEvent = useCallback((event: string) => {
    const next = readSnapshot();
    setSnapshot(next);
    setLog((prev) => {
      const entry: RotationLogEntry = {
        id: nextIdRef.current++,
        time: formatTime(),
        event,
        snapshot: next,
      };
      return [entry, ...prev].slice(0, MAX_LOG_ENTRIES);
    });
  }, []);

  const clearLog = useCallback(() => setLog([]), []);

  const copyLog = useCallback(async () => {
    const header = `userAgent: ${navigator.userAgent}`;
    const body = [...log]
      .reverse()
      .map(snapshotToText)
      .join('\n');
    await navigator.clipboard.writeText(`${header}\n${body}`);
  }, [log]);

  useEffect(() => {
    pushEvent('mount');

    const handlers: Array<[EventTarget | undefined, string, () => void]> = [];
    const register = (
      target: EventTarget | undefined,
      type: string,
      label: string,
    ) => {
      if (!target) return;
      const handler = () => pushEvent(label);
      target.addEventListener(type, handler);
      handlers.push([target, type, handler]);
    };

    // `resize` can fire many times per second (window drag, address-bar
    // collapse), which would flood the log and bury the orientation/fullscreen
    // events we actually care about. Coalesce it to one entry per frame; the
    // other events stay immediate.
    let resizeFrame = 0;
    const handleResize = () => {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0;
        pushEvent('resize');
      });
    };
    window.addEventListener('resize', handleResize);

    // `deviceorientation` (gamma) can move the resolved rotation and rewrite the
    // --force-rotate-angle CSS var WITHOUT firing any of the events above, so
    // without this the live snapshot goes stale on the exact bug we built this
    // page to diagnose. Coalesce through rAF so the read happens after the
    // production useOrientationState handler has synchronously written the var,
    // and only log when the resolved rotation actually flips (a raw ~60Hz tilt
    // stream would otherwise bury every other event in the 40-entry log).
    let tiltFrame = 0;
    const handleDeviceOrientation = () => {
      if (tiltFrame) return;
      tiltFrame = window.requestAnimationFrame(() => {
        tiltFrame = 0;
        const next = readSnapshot();
        setSnapshot(next);
        setLog((prev) => {
          if (prev[0]?.snapshot.forceRotateAngle === next.forceRotateAngle) {
            return prev;
          }
          const entry: RotationLogEntry = {
            id: nextIdRef.current++,
            time: formatTime(),
            event: 'deviceorientation',
            snapshot: next,
          };
          return [entry, ...prev].slice(0, MAX_LOG_ENTRIES);
        });
      });
    };
    window.addEventListener('deviceorientation', handleDeviceOrientation);

    register(window, 'orientationchange', 'orientationchange');
    register(document, 'fullscreenchange', 'fullscreenchange');
    register(window.screen?.orientation, 'change', 'screen.orientation change');

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener('deviceorientation', handleDeviceOrientation);
      if (tiltFrame) window.cancelAnimationFrame(tiltFrame);
      for (const [target, type, handler] of handlers) {
        target?.removeEventListener(type, handler);
      }
    };
  }, [pushEvent]);

  return { snapshot, log, pushEvent, clearLog, copyLog };
}
